import React, { useEffect } from "react";

// SETTING CONSTANTS: You may change this as you like
// without knowledge of the code below
const AMOUNTS = {
  monthly: [15, 30, 50, 100],
  quarterly: [45, 90, 150, 300],
  semestral: [90, 180, 300, 600],
  yearly: [180, 360, 600, 1200],
};
const DEFAULT_PRICES_PER_TONNE = { chf: 25, eur: 26.3, gbp: 22.4, usd: 27.6 };
const CUSTOM_TRANSLATIONS = {
  de: (tonnes) =>
    `Sie leisten einen Klimaschutzbeitrag in Höhe von <b>${tonnes} t</b> CO₂ eq.`,
  en: (tonnes) =>
    `You make a climate protection contribution amounting to <b>${tonnes} t</b> CO₂ eq.`,
  fr: (tonnes) =>
    `Vous apportez une contribution à la protection du climat à hauteur de <b>${tonnes} t</b> CO₂ eq.`,
  it: (tonnes) =>
    `Il loro contributo alla protezione del clima è pari a <b>${tonnes} t</b> CO₂ eq.`,
};
// END SETTING CONSTANTS

// translates the constants above and the params into tamaro setting format
const tamaroSettings = (
  portfolio,
  availableCurrencies,
  defaultCurrency,
  language,
  testMode
) => {
  return {
    language: language,
    payment_types: ["recurring"], // no one-time payment
    recurring_intervals: Object.keys(AMOUNTS),
    defaultRecurringInterval: "monthly",
    currencies: availableCurrencies,
    showStoredCustomerDonationReceipt: false,
    defaultCurrency: defaultCurrency.toLowerCase(),
    paymentFormPrefill: {
      stored_portfolio: portfolio.key || "myclimate",
    },
    amounts: Object.keys(AMOUNTS).map((recurring_interval) => {
      return {
        if: `recurringInterval() == ${recurring_interval}`,
        then: AMOUNTS[recurring_interval],
      };
    }),
    minimumCustomAmount: 1,
    //translations: tamaroTranslations(),
    translations: Object.keys(CUSTOM_TRANSLATIONS).reduce((map, lang) => {
      // the content of this translation key will be set dynamically
      // why co2_info_temp? see event handler below
      map[lang] = {
        co2_info: "",
        co2_info_temp: "",
        blocks: {
          payment_amounts_and_intervals: {
            payment_types: { change_interval: "⬇︎" },
          },
          payment_cover_fee: {
            title: lang == "de" ? "Zahlungsgebühren" : undefined,
            legend: lang == "de" ? "Zahlungsgebühren" : undefined,
          },
        },
        payment_form: {
          stored_cover_transaction_fee:
            lang == "de"
              ? "Ja, ich möchte die Zahlungsgebühren von %% toUpperCase(currency()) %% %% fee() %% übernehmen, um sicherzustellen, dass 100 % meiner Spenden an %% trans(organization_name) %% gehen"
              : undefined,
        },
      };
      return map;
    }, {}),
    testMode: testMode,

    // /////// uncomment for debugging
    // debug: true,
    // layout: 'list',
    // overlayMode: 'outer',
    // debugSlots: true,
    // debugErrorMessages: true,
    // debugStyles: true,
    // //////

    // add reactive html content below the amounts choice buttons
    slots: {
      amounts_end: [
        {
          component: "content",
          text_html: "co2_info", // = translation key
        },
      ],
    },
  };
};

// called when the tamaro script has been loaded
const runTamaroWidget = (
  portfolio,
  availableCurrencies,
  currency,
  tonPrices,
  language,
  testMode
) => {
  window.rnw.tamaro.runWidget(
    ".rnw-widget-container",
    tamaroSettings(portfolio, availableCurrencies, currency, language, testMode)
  );

  // event handler that updates the co2 info text below the amount choices
  const updateCo2Info = (e) => {
    const paymentForm = e.data.api.paymentForm;
    const translations = e.data.api.config.translations;
    const price = tonPrices[paymentForm.getCurrency()];
    const tons = (paymentForm.getAmount() / price).toFixed(1);
    Object.keys(CUSTOM_TRANSLATIONS).forEach((language) => {
      const msg = CUSTOM_TRANSLATIONS[language](tons);
      translations[language].co2_info = msg;
      translations[language].co2_info_temp = msg;
    });
    // this is stupid but in tamaro, we have to set the translation key again
    // to trigger a reactive update
    const co2Info = e.data.api.config.slots.amounts_end[0];
    co2Info.text_html = "co2_info_temp";
    setTimeout(() => {
      co2Info.text_html = "co2_info";
    });
  };

  window.rnw.tamaro.events["currencyChanged"].subscribe(updateCo2Info);
  window.rnw.tamaro.events["amountChanged"].subscribe(updateCo2Info);
  window.rnw.tamaro.events["afterCreate"].subscribe(updateCo2Info);
};

const addTamaroWidget = (
  portfolio,
  availableCurrencies,
  currency,
  tonPrices,
  language,
  testMode
) => {
  const script = document.createElement("script");
  script.async = true;
  script.setAttribute("data-source", "raisenow");
  script.src = "https://tamaro.raisenow.com/myclimate-ch/latest/widget.js";
  document.body.appendChild(script);
  script.addEventListener("load", (event) => {
    runTamaroWidget(
      portfolio,
      availableCurrencies,
      currency,
      tonPrices,
      language,
      testMode
    );
  });
};

const removeTamaroWidget = () => {
  document
    .querySelectorAll('[data-source="raisenow"]')
    .forEach((script) => script.parentNode.removeChild(script));
  window.rnw = undefined;
  const container = document.querySelector(".rnw-widget-container");
  if (container) {
    container.innerHTML = "";
    // Remove all event listeners
    const old_element = container;
    var new_element = old_element.cloneNode(true);
    old_element.parentNode.replaceChild(new_element, old_element);
  }
};

const MyclimateSubscription = ({ config }) => {
  useEffect(() => {
    const testMode = config.test || process.env.NODE_ENV !== "production";
    let availableCurrencies = Object.keys(DEFAULT_PRICES_PER_TONNE);
    let mergedTonnePrices = availableCurrencies.reduce((out, key) => {
      out[key] = config.tonPrices[key] || DEFAULT_PRICES_PER_TONNE[key];
      return out;
    }, {});
    addTamaroWidget(
      "myclimate",
      availableCurrencies,
      config.currency,
      mergedTonnePrices,
      config.language || "en",
      testMode
    );
    return () => {
      // react calls this when component is unmounting
      removeTamaroWidget();
    };
  });

  return <div className="rnw-widget-container"></div>;
};
export default MyclimateSubscription;
